import './style/editProductCosts.scss';
import classNames from 'classnames';
import React, {FocusEventHandler, MouseEventHandler, PropsWithChildren, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AwesomeIcon, ChangeKeyFunction, Loader, preventDefault} from '@dvrd/dvr-controls';
import ProductCostsV2, {ProductCostsV2Fields} from '../../../../../models/productCostsV2Model';
import IDate from '@dvrd/idate';
import {LVBSize} from '../../../../../util/enums';
import {ProductCostsErrors} from '../../../../../util/types';
import {BolmateButton, BolmateInput, BolmateNumberInput, BolmateSelect, DvrdDatePicker} from '../../../../controls';
import {BolmateSelectItem} from '../../../../../util/interfaces';

interface Props {
    onClose: MouseEventHandler;
    onChange: ChangeKeyFunction<keyof ProductCostsV2Fields, number | IDate | string | LVBSize | null>;
    onResetError: (key: keyof ProductCostsErrors) => FocusEventHandler;
    onSubmit: MouseEventHandler;
    onDelete: (costs: ProductCostsV2) => MouseEventHandler;
    productCosts: ProductCostsV2 | null;
    loading: boolean;
    errors: ProductCostsErrors;
}

function useLVBItems(): Array<BolmateSelectItem<LVBSize>> {
    const {t} = useTranslation();
    return [
        {value: '', label: t('salesDashboardV2.productDetails.productCosts.unknown', 'Onbekend'), selectable: false},
        {value: LVBSize.XXXS, label: 'XXXS'},
        {value: LVBSize.XXS, label: 'XXS'},
        {value: LVBSize.XS, label: 'XS'},
        {value: LVBSize.S, label: 'S'},
        {value: LVBSize.M, label: 'M'},
        {value: LVBSize.L, label: 'L'},
        {value: LVBSize.XL, label: 'XL'},
        {value: LVBSize.XXL, label: 'XXL'},
        {value: LVBSize.XXXL_1, label: t('salesDashboardV2.productDetails.productCosts.xxxl1', 'XXXL 1-mans')},
        {value: LVBSize.XXXL_2, label: t('salesDashboardV2.productDetails.productCosts.xxxl2', 'XXXL 2-mans')}
    ];
}

const euroOrnament = {element: <span className='euro-ornament'>&euro;</span>};

type SectionKey = 'general' | 'fbb' | 'fbr';

interface SectionProps {
    onToggle: (key: SectionKey) => MouseEventHandler;
    sectionKey: SectionKey;
    title: string;
    open: boolean;
    className?: string;
}

function CostsSection(props: PropsWithChildren<SectionProps>) {
    const {onToggle, sectionKey, title, open, children, className} = props;

    return (
        <section className={classNames('costs-section', open && 'open', className)}>
            <div className='section-header'>
                <label className='section-label' onClick={onToggle(sectionKey)}>
                    {title}
                </label>
                <AwesomeIcon name='chevron-down' className='chev-icon' />
            </div>
            <div className='section-content'>{children}</div>
        </section>
    );
}

export default function EditProductCosts(props: Props) {
    const {t} = useTranslation();
    const LVBItems = useLVBItems();
    const {onClose, onChange, onResetError, onSubmit, onDelete, productCosts, loading, errors} = props;
    const [openSections, setOpenSections] = useState<Set<SectionKey>>(new Set(['general']));

    function onToggleSection(key: SectionKey) {
        return function () {
            const sections = new Set(openSections);
            if (sections.has(key)) sections.delete(key);
            else sections.add(key);
            setOpenSections(sections);
        };
    }

    function renderForm() {
        return (
            <form className='product-costs-form' onSubmit={preventDefault}>
                <Loader active={loading} />
                <div className='costs-header'>
                    <DvrdDatePicker
                        onChange={onChange('start_date')}
                        value={productCosts?.startDateAsDate ?? IDate.now()}
                        label={t('salesDashboardV2.productDetails.productCosts.validFrom', 'Geldig vanaf')}
                        placeholder={t('salesDashboardV2.productDetails.productCosts.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        className='start-date'
                        closeOnChange
                    />
                    {!!productCosts?.id.length && (
                        <AwesomeIcon name='trash-alt' onClick={onDelete(productCosts)} className='btn-delete' />
                    )}
                </div>
                <CostsSection
                    onToggle={onToggleSection}
                    sectionKey={'general'}
                    title={t('salesDashboardV2.productDetails.productCosts.general', 'Algemeen')}
                    open={openSections.has('general')}
                >
                    <BolmateNumberInput
                        onChange={onChange('min_quantity')}
                        onFocus={onResetError('minQuantity')}
                        value={productCosts?.minQuantity ?? 1}
                        label={t(
                            'salesDashboardV2.productDetails.productCosts.fromQuantity',
                            'Vanaf aantal producten *'
                        )}
                        error={errors.minQuantity}
                        asNumber
                        wholeNumbers
                    />
                    <BolmateNumberInput
                        onChange={onChange('cost_price')}
                        value={productCosts?.costPrice ?? ''}
                        label={t('salesDashboardV2.productDetails.productCosts.costPrice', 'Kostprijs')}
                        ornaments={euroOrnament}
                        asNumber
                        asCurrency
                    />
                    <BolmateNumberInput
                        onChange={onChange('misc_return_costs')}
                        value={productCosts?.miscReturnCosts ?? ''}
                        label={t('salesDashboardV2.productDetails.productCosts.returnCosts', 'Retour kosten')}
                        ornaments={euroOrnament}
                        asNumber
                        asCurrency
                    />
                    <BolmateInput
                        onChange={onChange('misc_return_description')}
                        value={productCosts?.miscReturnDescription ?? ''}
                        label={t(
                            'salesDashboardV2.productDetails.productCosts.returnCostsDesc',
                            'Omschrijving retour kosten'
                        )}
                    />
                    <BolmateNumberInput
                        onChange={onChange('misc_costs')}
                        value={productCosts?.miscCosts ?? ''}
                        label={t('salesDashboardV2.productDetails.productCosts.miscCosts', 'Overige kosten')}
                        ornaments={euroOrnament}
                        asNumber
                        asCurrency
                    />
                    <BolmateInput
                        onChange={onChange('misc_costs_description')}
                        value={productCosts?.miscCostsDescription ?? ''}
                        label={t(
                            'salesDashboardV2.productDetails.productCosts.miscCostsDesc',
                            'Omschrijving overige kosten'
                        )}
                    />
                </CostsSection>
                <CostsSection
                    onToggle={onToggleSection}
                    sectionKey={'fbb'}
                    title={t('salesDashboardV2.productDetails.productCosts.lvbShipping', 'LVB verzending')}
                    open={openSections.has('fbb')}
                    className='fw'
                >
                    <BolmateSelect
                        items={LVBItems}
                        value={productCosts?.lvbSize ?? ''}
                        onChange={onChange('lvb_size')}
                        label={t(
                            'salesDashboardV2.productDetails.productCosts.lvbSizeAuto',
                            'LVB formaat (automatisch bepaald)'
                        )}
                        itemsHeight='15rem'
                    />
                </CostsSection>
                <CostsSection
                    onToggle={onToggleSection}
                    sectionKey={'fbr'}
                    title={t('salesDashboardV2.productDetails.productCosts.ownShipping', 'Eigen verzending')}
                    open={openSections.has('fbr')}
                >
                    <BolmateNumberInput
                        onChange={onChange('pick_costs')}
                        value={productCosts?.pickCosts ?? ''}
                        label={t('salesDashboardV2.productDetails.productCosts.pickPack', 'Pick & Pack kosten')}
                        ornaments={euroOrnament}
                        asNumber
                        asCurrency
                    />
                    <BolmateNumberInput
                        onChange={onChange('send_costs')}
                        value={productCosts?.sendCosts ?? ''}
                        label={t('salesDashboardV2.productDetails.productCosts.sendCosts', 'Verzendkosten')}
                        ornaments={euroOrnament}
                        asNumber
                        asCurrency
                    />
                </CostsSection>
            </form>
        );
    }

    return (
        <div className={classNames('edit-product-costs', !!productCosts && 'open')}>
            {renderForm()}
            <div className='footer-container'>
                <BolmateButton
                    onClick={onClose}
                    label={t('salesDashboardV2.productDetails.productCosts.back', 'Terug')}
                    ornaments={{element: <AwesomeIcon name='arrow-left' />}}
                    disabled={loading}
                    secondary
                />
                <BolmateButton
                    onClick={onSubmit}
                    label={t('salesDashboardV2.productDetails.productCosts.save', 'Opslaan')}
                    disabled={loading}
                />
            </div>
        </div>
    );
}
